import React, { useContext } from 'react';
import List from '@mui/material/List';
import { useTranslation } from 'react-i18next';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CategoryIcon from '@mui/icons-material/Category';
import ListIcon from '@mui/icons-material/List';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ListSubheader from '@mui/material/ListSubheader';
import InfoIcon from '@mui/icons-material/Info';
import CodeIcon from '@mui/icons-material/Code';
import AppsIcon from '@mui/icons-material/Apps';
import DescriptionIcon from '@mui/icons-material/Description';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleIcon from '@mui/icons-material/People';
import PostAddIcon from '@mui/icons-material/PostAdd';
import WebIcon from '@mui/icons-material/Web';
import Context from '../../context';
import DrawerListItem from '../../components/_header/DrawerListItem';
import useRoutes from '../../components/_hook/useRoutes';
import useRouter from '../../components/_hook/useRouter';

export default function ProductDrawer() {
  const { path } = useRoutes();
  const { params, routeName } = useRouter();
  const { docID } = params;
  const { company } = useContext(Context);
  const { t } = useTranslation();

  const productDetailRoutes = [
    'product.information',
    'product.variation',
    'product.description',
    'product.ecommerce',
    'product.inventory',
    'product.packaging',
    'product.shipment',
    'product.media',
    'product.developer.overwrite',
    'product.developer.schemaMarkup',
    'product.subdirectory',
  ];

  const dropshippingProductRoutes = [
    'product.dropshipping.list',
    'product.dropshipping.price',
    'product.dropshipping.inventory',
    'product.dropshipping.dropshipper',
    'product.dropshipping.dropshipper.approved',
    'product.dropshipping.dropshipper.approved.detail',
    'product.dropshipping.dropshipper.disapproved',
    'product.dropshipping.dropshipper.pending',
    'product.dropshipping.dropshipper.pending.detail',
  ];

  const dropshippedProductRoutes = [
    'product.dropshipped.list',
    'product.dropshipped.vendor',
    'product.dropshipped.vendor.detail',
    'product.dropshipped.category',
    'product.dropshipped.price',
    'product.dropshipped.request',
    'product.dropshipped.request.detail',
  ];

  return (
    <>
      <List>
        <DrawerListItem
          path={path('product.dashboard', {
            companyID: company._id,
          })}
          primaryText={t('dashboard')}
          icon={<DashboardIcon />}
        />
        <DrawerListItem
          path={path('product.categories', {
            companyID: company._id,
          })}
          primaryText={t('category')}
          icon={<CategoryIcon />}
          className="product-drawer-category-list-item-joyride"
        />
        <DrawerListItem
          path={path('product.list', {
            companyID: company._id,
          })}
          primaryText={t('product')}
          icon={<ListIcon />}
          className="product-drawer-product-list-item-joyride"
        />
        {company && company.settings && company.settings.enableDropshipping && (
          <>
            <DrawerListItem
              path={path('product.dropshipping.list', {
                companyID: company._id,
              })}
              primaryText={t('dropshipping_product', { count: 2 })}
              icon={<ListIcon />}
            />
            <DrawerListItem
              path={path('product.dropshipped.list', {
                companyID: company._id,
              })}
              primaryText={t('dropshipped_product', { count: 2 })}
              icon={<ListIcon />}
            />
          </>
        )}
        <DrawerListItem
          path={path('product.import', {
            companyID: company._id,
          })}
          primaryText={t('import_product')}
          icon={<ImportExportIcon />}
          className="product-drawer-import-product-list-item-joyride"
        />
        <DrawerListItem
          path={path('product.import.image', {
            companyID: company._id,
          })}
          primaryText={t('import_image')}
          icon={<ImportExportIcon />}
          className="product-drawer-import-image-list-item-joyride"
        />
        {docID && productDetailRoutes.includes(routeName) && (
          <>
            <ListSubheader>
              {t('product')} {t('detail')}
            </ListSubheader>
            <DrawerListItem
              path={path('product.information', {
                companyID: company._id,
                docID,
              })}
              primaryText={t('information')}
              icon={<InfoIcon />}
            />
            <DrawerListItem
              path={path('product.variation', {
                companyID: company._id,
                docID,
              })}
              primaryText={t('variation', { count: 2 })}
              icon={<AppsIcon />}
            />
            <DrawerListItem
              path={path('product.description', {
                companyID: company._id,
                docID,
              })}
              primaryText={t('description')}
              icon={<DescriptionIcon />}
            />

            <DrawerListItem
              path={path('product.ecommerce', {
                companyID: company._id,
                docID,
              })}
              primaryText={t('ecommerce')}
              icon={<ShoppingCartIcon />}
            />
            <DrawerListItem
              path={path('product.inventory', {
                companyID: company._id,
                docID,
              })}
              primaryText={t('inventory')}
              icon={<AllInboxIcon />}
            />
            <DrawerListItem
              path={path('product.packaging', {
                companyID: company._id,
                docID,
              })}
              primaryText={t('packaging')}
              icon={<ShoppingBasketIcon />}
            />
            <DrawerListItem
              path={path('product.shipment', {
                companyID: company._id,
                docID,
              })}
              primaryText={t('shipment')}
              icon={<LocalShippingIcon />}
            />
            <DrawerListItem
              path={path('product.media', {
                companyID: company._id,
                docID,
              })}
              primaryText={t('media')}
              icon={<ListIcon />}
            />
            <DrawerListItem
              path={path('product.subdirectory', {
                companyID: company._id,
                docID,
              })}
              primaryText={t('subdirectory')}
              icon={<WebIcon />}
            />
            <ListSubheader>
              {t('product')} {t('detail')} {t('developer')}
            </ListSubheader>
            <DrawerListItem
              disabled={docID === 'new'}
              path={path('product.developer.overwrite', {
                companyID: company._id,
                docID,
              })}
              primaryText={t('overwrite')}
              icon={<CodeIcon />}
            />
            <DrawerListItem
              disabled={docID === 'new'}
              path={path('product.developer.schemaMarkup', {
                companyID: company._id,
                docID,
              })}
              primaryText={t('schema_markup')}
              icon={<CodeIcon />}
            />
          </>
        )}
        {dropshippingProductRoutes.includes(routeName) && (
          <>
            <ListSubheader>{t('dropshipping_product')}</ListSubheader>
            <DrawerListItem
              path={path('product.dropshipping.inventory', {
                companyID: company._id,
              })}
              primaryText={t('inventory_management')}
              icon={<AllInboxIcon />}
            />
            <DrawerListItem
              path={path('product.dropshipping.price', {
                companyID: company._id,
              })}
              primaryText={t('price_management')}
              icon={<AttachMoneyIcon />}
            />
            <DrawerListItem
              path={path('product.dropshipping.dropshipper', {
                companyID: company._id,
              })}
              primaryText={t('dropshipper_list')}
              icon={<ListIcon />}
            />
            {[
              'product.dropshipping.dropshipper',
              'product.dropshipping.dropshipper.approved',
              'product.dropshipping.dropshipper.approved.detail',
              'product.dropshipping.dropshipper.disapproved',
              'product.dropshipping.dropshipper.pending',
              'product.dropshipping.dropshipper.pending.detail',
            ].includes(routeName) && (
              <>
                <ListSubheader>{t('dropshipper_list')}</ListSubheader>
                <DrawerListItem
                  path={path('product.dropshipping.dropshipper.approved', {
                    companyID: company._id,
                  })}
                  primaryText={t('approved_dropshipper')}
                  icon={<ListIcon />}
                />
                <DrawerListItem
                  path={path('product.dropshipping.dropshipper.disapproved', {
                    companyID: company._id,
                  })}
                  primaryText={t('disapproved_dropshipper')}
                  icon={<ListIcon />}
                />
                <DrawerListItem
                  path={path('product.dropshipping.dropshipper.pending', {
                    companyID: company._id,
                  })}
                  primaryText={t('pending_dropshipper')}
                  icon={<ListIcon />}
                />
              </>
            )}
          </>
        )}
        {dropshippedProductRoutes.includes(routeName) && (
          <>
            <ListSubheader>{t('dropshipped_product')}</ListSubheader>
            <DrawerListItem
              path={path('product.dropshipped.vendor', {
                companyID: company._id,
              })}
              primaryText={t('dropshipping_vendor')}
              icon={<PeopleIcon />}
            />
            <DrawerListItem
              path={path('product.dropshipped.category', {
                companyID: company._id,
              })}
              primaryText={t('category_assignment')}
              icon={<CategoryIcon />}
            />
            <DrawerListItem
              path={path('product.dropshipped.price', {
                companyID: company._id,
              })}
              primaryText={t('price_management')}
              icon={<AttachMoneyIcon />}
            />
            <DrawerListItem
              path={path('product.dropshipped.request', {
                companyID: company._id,
              })}
              primaryText={t('request_for_dropship')}
              icon={<PostAddIcon />}
            />
          </>
        )}
      </List>
    </>
  );
}
